import { op, Operation } from "libs/transaction";
import { getPointer, ThreadSubscriptionPreference } from "libs/schema";
import { createAction } from "./action";
import { undeleteOperation } from "./utils/operations";
import { authorizeUserHasAccessToThread } from "./authorizers/threadAuthorizers";

/* -------------------------------------------------------------------------------------------------
 *  updateThreadSubscription
 * -------------------------------------------------------------------------------------------------
 */

type UpdateThreadSubscriptionProps = {
  threadId: string;
  preference: ThreadSubscriptionPreference;
  ownerOrganizationId: string;
};

export const updateThreadSubscription = createAction<UpdateThreadSubscriptionProps>(
  "updateThreadSubscription.updateThreadSubscription",
  {
    operations: async (props, context) => {
      const { ownerOrganizationId, preference, threadId } = props;

      const operations: Operation[] = [];

      const subscriptionPointer = getPointer("thread_subscription", {
        thread_id: threadId,
        user_id: context.currentUserId,
      });

      const participationPointer = getPointer("thread_user_participant", {
        thread_id: threadId,
        user_id: context.currentUserId,
      });

      operations.push(
        op.set("thread_subscription", {
          id: subscriptionPointer.id,
          owner_organization_id: ownerOrganizationId,
          preference,
          thread_id: threadId,
          user_id: context.currentUserId,
        }),
        // It's important that this upsert comes after setting the `thread_subscription`
        // so that the subscription preference is set already. This way the "upsert_on_update"
        // operation's `where` clause will be evaluated against the new subscription
        // preference.
        op.upsert(subscriptionPointer, {
          onUpdate: [
            {
              type: "upsert_on_update",
              where: { preference: { eq: "involved" } },
              operations: [op.delete(participationPointer)],
            },
          ],
        }),
      );

      return operations;
    },
    authorize: async (props, context) => {
      await authorizeUserHasAccessToThread({ threadId: props.threadId, context });
    },
  },
);

/* -------------------------------------------------------------------------------------------------
 *  restoreThreadSubscription
 * -------------------------------------------------------------------------------------------------
 */

type RestoreThreadSubscriptionProps = {
  threadId: string /** The preference property we want to restore the subscription state to */;
  preference: ThreadSubscriptionPreference | null;
  /** The participation property we want to restore the subscription state to */
  isParticipating: boolean;
};

export const restoreThreadSubscription = createAction<RestoreThreadSubscriptionProps>(
  "updateThreadSubscription.restoreThreadSubscription",
  {
    operations: async (props, context) => {
      const { isParticipating, preference, threadId } = props;

      const operations: Operation[] = [];

      const subscriptionPointer = getPointer("thread_subscription", {
        thread_id: threadId,
        user_id: context.currentUserId,
      });

      const participationPointer = getPointer("thread_user_participant", {
        thread_id: threadId,
        user_id: context.currentUserId,
      });

      if (preference) {
        operations.push(op.update(subscriptionPointer, { preference }));
      } else {
        operations.push(op.delete(subscriptionPointer));
      }

      if (isParticipating) {
        operations.push(undeleteOperation(participationPointer));
      }

      return operations;
    },
    authorize: async (props, context) => {
      await authorizeUserHasAccessToThread({ threadId: props.threadId, context });
    },
  },
);

export const actions = {
  updateThreadSubscription,
  restoreThreadSubscription,
};
